// Colyseus state schema — the replicated entity shapes. @colyseus/schema is
// the wire-format library (engine-agnostic, runs in Node and the browser);
// renderer code never appears here.
//
// Deliberately defined via defineTypes() rather than @type() decorators:
// this package is compiled by three different toolchains (tsx for the dev
// server, Vite for the web client, esbuild for the production bundle), and
// decorator emit differs across them unless every one is configured in
// lockstep. Plain classes with `declare` fields (which emit nothing) and
// constructor defaults behave identically everywhere. The schema round-trip
// test guards this.

import { defineTypes, MapSchema, Schema, view } from "@colyseus/schema";

export class PlayerState extends Schema {
  declare name: string;
  /**
   * Appearance as a JSON string (shape: shared/appearance.ts). It changes
   * at most once per join, so a single string field beats ten schema
   * fields; clients parse with parseAppearance (never throws).
   */
  declare appearance: string;
  declare x: number;
  declare y: number;
  declare z: number;
  /** Facing direction in radians, [-π, π]. */
  declare yaw: number;
  /** Skiff id while riding, else "". Pose mirrors the skiff meanwhile. */
  declare mounted: string;
  /**
   * How this body is carrying itself — a Carriage token (shared/carriage.ts),
   * "" when nothing is wrong. Deliberately PUBLIC rather than private to
   * its owner, for two reasons that both matter: a Medic's whole perception
   * is reading trouble on other people at a glance, and movement caps on
   * it, so the client must have it to predict its own gait without
   * rubber-banding. It is a word, never a number — the wire carries no
   * severity value at all, and at the deep end the token deliberately
   * stops saying which hazard is doing it, because knowing that is the
   * Medic's product.
   */
  declare carriage: string;

  constructor() {
    super();
    this.name = "";
    this.appearance = "";
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.yaw = 0;
    this.mounted = "";
    this.carriage = "";
  }
}

defineTypes(PlayerState, {
  name: "string",
  appearance: "string",
  x: "float32",
  y: "float32",
  z: "float32",
  yaw: "float32",
  mounted: "string",
  carriage: "string",
});

/**
 * A hover skiff on the surface — the replicated PROJECTION of a persistent
 * `equipment` row (keyed by the row's own id), maintained by whichever
 * room is simulating the planet. The room renders it; the row owns it.
 *
 * Wear and charge cross the wire only as BAND words (shared/equipment.ts);
 * the integers behind them are server-only and absent from the protocol,
 * so no future panel can accidentally print them.
 */
export class SkiffState extends Schema {
  declare x: number;
  declare z: number;
  declare yaw: number;
  /** Signed speed along the nose, m/s (clients scale dust/bob by it). */
  declare speed: number;
  /** Rider sessionId, or "" while parked. */
  declare riderId: string;
  /** Passenger sessionId, or "" while the second seat is empty. */
  declare passengerId: string;
  /** The plotted run's placard — a destination label, or "". Words the
   *  server composed; the client must render NO bearing toward it. */
  declare plotted: string;
  /** Wear band word (shared/equipment.ts) — never a number. */
  declare wearBand: string;
  /** Charge band word — "dry" is the only state that refuses throttle. */
  declare chargeBand: string;
  /** Owner's display name for prompts, or "" while unowned. */
  declare ownerName: string;
  /** Whether a stranger may board (owners can open their skiff). */
  declare locked: boolean;
  /** The settlement's shared loaner: always open, never wears. */
  declare town: boolean;

  constructor() {
    super();
    this.x = 0;
    this.z = 0;
    this.yaw = 0;
    this.speed = 0;
    this.riderId = "";
    this.passengerId = "";
    this.plotted = "";
    this.wearBand = "true";
    this.chargeBand = "full";
    this.ownerName = "";
    this.locked = false;
    this.town = false;
  }
}

defineTypes(SkiffState, {
  x: "float32",
  z: "float32",
  yaw: "float32",
  speed: "float32",
  riderId: "string",
  passengerId: "string",
  plotted: "string",
  wearBand: "string",
  chargeBand: "string",
  ownerName: "string",
  locked: "boolean",
  town: "boolean",
});

/**
 * A ship — the replicated PROJECTION of a persistent `equipment` row
 * (keyed by the row's id), maintained by whichever room it stands or
 * flies in. Same contract as SkiffState, plus the vertical axis and the
 * aether-pressure manifold.
 *
 * Wear and charge cross the wire only as BAND words. The three manifold
 * meters are floats because the flight instrument binds needles to real
 * telemetry (the §e sanction) — they are flight state, not progression,
 * and the client renders them only as analog gauge language, never digits.
 */
export class ShipState extends Schema {
  declare x: number;
  /** Altitude — absolute scene y, metres. */
  declare y: number;
  declare z: number;
  declare yaw: number;
  /** Signed speed along the nose, m/s. */
  declare speed: number;
  /** Vertical rate, m/s. */
  declare vy: number;
  /** Rider sessionId, or "" while parked. */
  declare riderId: string;
  /** Passenger sessionId, or "" while the second seat is empty. */
  declare passengerId: string;
  /** The plotted run's placard — a destination label, or "". */
  declare plotted: string;
  /** Wear band word (shared/equipment.ts) — never a number. */
  declare wearBand: string;
  /** Charge band word — "dry" refuses throttle and climb. */
  declare chargeBand: string;
  /** Owner's display name for prompts, or "" while unowned. */
  declare ownerName: string;
  declare locked: boolean;
  /** Where the pressure is routed — a PowerFocus word (shared/ship.ts). */
  declare focus: string;
  /** Surge tank 0..1. */
  declare boost: number;
  /** Gun-coil pressure 0..1 (combat consumes; exists and regens now). */
  declare coil: number;
  /** The veil's knit 0..1 (combat consumes; exists and regens now). */
  declare veil: number;
  /** Whether the boost is live this instant (drives the burn visuals). */
  declare boosting: boolean;
  /**
   * The manifold has tripped: no throttle, no climb, drifting until the
   * re-light. Replicated so the pilot's prediction coasts identically
   * and every viewer sees a dead ship read dead.
   */
  declare adrift: boolean;
  /**
   * How close the manifold is to tripping, 0..1 — accrues only from hits
   * on a bare hull (the veil down). Telemetry for the instrument's red
   * arc; flight state, not progression, and never a digit on screen.
   */
  declare strain: number;

  constructor() {
    super();
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.yaw = 0;
    this.speed = 0;
    this.vy = 0;
    this.riderId = "";
    this.passengerId = "";
    this.plotted = "";
    this.wearBand = "true";
    this.chargeBand = "full";
    this.ownerName = "";
    this.locked = false;
    this.focus = "even";
    this.boost = 0;
    this.coil = 1;
    this.veil = 1;
    this.boosting = false;
    this.adrift = false;
    this.strain = 0;
  }
}

defineTypes(ShipState, {
  x: "float32",
  y: "float32",
  z: "float32",
  yaw: "float32",
  speed: "float32",
  vy: "float32",
  riderId: "string",
  passengerId: "string",
  plotted: "string",
  wearBand: "string",
  chargeBand: "string",
  ownerName: "string",
  locked: "boolean",
  focus: "string",
  boost: "float32",
  coil: "float32",
  veil: "float32",
  boosting: "boolean",
  adrift: "boolean",
  strain: "float32",
});

/**
 * A warden — the replicated projection of a server-simulated rogue
 * automaton. Position, disposition word, integrity word. The integrity
 * INTEGER never crosses the wire, exactly as wear and charge do not.
 */
export class WardenState extends Schema {
  declare x: number;
  declare y: number;
  declare z: number;
  declare yaw: number;
  /** Disposition word: dormant | hunting | adrift. */
  declare state: string;
  /** Integrity word: whole | scored | riven | adrift. */
  declare band: string;

  constructor() {
    super();
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.yaw = 0;
    this.state = "dormant";
    this.band = "whole";
  }
}

defineTypes(WardenState, {
  x: "float32",
  y: "float32",
  z: "float32",
  yaw: "float32",
  state: "string",
  band: "string",
});

/**
 * A creature on the surface — the replicated PROJECTION of a server-side
 * wilds record, maintained by the planet room. Pose, a species word, and
 * a posture word are the entire wire surface: no vigor, no threat table,
 * no target, no number of any kind. A player reads a creature the way a
 * person reads an animal — silhouette, posture, and closing speed — and
 * the renderer needs nothing more than that either.
 */
export class CreatureState extends Schema {
  /** Species token (shared/creatures.ts). */
  declare species: string;
  declare x: number;
  declare z: number;
  /** Facing direction in radians, [-π, π]. */
  declare yaw: number;
  /** Ground speed, m/s — drives the gait animation, nothing else. */
  declare speed: number;
  /** Posture token (shared/creatures.ts) — a word, never a number. */
  declare posture: string;

  constructor() {
    super();
    this.species = "";
    this.x = 0;
    this.z = 0;
    this.yaw = 0;
    this.speed = 0;
    this.posture = "idle";
  }
}

defineTypes(CreatureState, {
  species: "string",
  x: "float32",
  z: "float32",
  yaw: "float32",
  speed: "float32",
  posture: "string",
});

export class ClubState extends Schema {
  /** Keyed by Colyseus sessionId. */
  declare players: MapSchema<PlayerState>;
  /** True while a DJ source is connected to this club's Icecast mount. */
  declare live: boolean;
  /** Stream metadata ("Artist — Title") while live; empty otherwise. */
  declare nowPlaying: string;
  /** Listeners on the Icecast mount (in-world players + radio listeners). */
  declare listeners: number;

  constructor() {
    super();
    this.players = new MapSchema<PlayerState>();
    this.live = false;
    this.nowPlaying = "";
    this.listeners = 0;
  }
}

defineTypes(ClubState, {
  players: { map: PlayerState },
  live: "boolean",
  nowPlaying: "string",
  listeners: "uint16",
});

/**
 * A homestead claim's stake on the surface — the replicated projection of a
 * `homestead_claims` row: the name on the plaque and whose mark it carries.
 * Words only; the claim's rules live server-side.
 */
export class ClaimState extends Schema {
  declare name: string;
  /** Holder's display name, resolved live (the row stores an id). */
  declare ownerName: string;
  declare x: number;
  declare z: number;

  constructor() {
    super();
    this.name = "";
    this.ownerName = "";
    this.x = 0;
    this.z = 0;
  }
}

defineTypes(ClaimState, {
  name: "string",
  ownerName: "string",
  x: "float32",
  z: "float32",
});

/**
 * A raised structure on the surface — projection of a
 * `homestead_structures` row. The exterior shell everyone collides with;
 * the interior is its own room reached through the door.
 */
export class StructureState extends Schema {
  /** A StructureKind (shared/housing.ts). */
  declare kind: string;
  declare x: number;
  declare z: number;
  /** Facing as a quarter turn (0..3) — footprints stay axis-aligned. */
  declare quarter: number;
  declare claimId: string;
  declare claimName: string;
  declare ownerName: string;
  /** Door policy word: "open" | "shut" | "invited". Never a list. */
  declare door: string;

  constructor() {
    super();
    this.kind = "cabin";
    this.x = 0;
    this.z = 0;
    this.quarter = 0;
    this.claimId = "";
    this.claimName = "";
    this.ownerName = "";
    this.door = "shut";
  }
}

defineTypes(StructureState, {
  kind: "string",
  x: "float32",
  z: "float32",
  quarter: "uint8",
  claimId: "string",
  claimName: "string",
  ownerName: "string",
  door: "string",
});

/**
 * A piece of furniture inside a home — projection of a
 * `homestead_placeables` row, keyed by its id. `makerName` is the
 * provenance seam crafted furniture plugs into: world-provided pieces
 * replicate "", a crafted piece will resolve its maker live, exactly as
 * artifacts do.
 */
export class PlaceableState extends Schema {
  /** A PlaceableKind (shared/housing.ts). */
  declare kind: string;
  declare cellX: number;
  declare cellZ: number;
  /** Rotation as a quarter turn (0..3). */
  declare rot: number;
  /** True while the owner is carrying it to a new spot. */
  declare carried: boolean;
  declare makerName: string;

  constructor() {
    super();
    this.kind = "cot";
    this.cellX = 0;
    this.cellZ = 0;
    this.rot = 0;
    this.carried = false;
    this.makerName = "";
  }
}

defineTypes(PlaceableState, {
  kind: "string",
  cellX: "int16",
  cellZ: "int16",
  rot: "uint8",
  carried: "boolean",
  makerName: "string",
});

/**
 * A home interior room. Small and fully replicated — no view filtering in
 * a room the size of a cabin.
 */
export class HomeState extends Schema {
  declare players: MapSchema<PlayerState>;
  /** The claim's name — what the room calls itself. */
  declare label: string;
  declare ownerName: string;
  /** Door policy word: "open" | "shut" | "invited". */
  declare door: string;
  /** Keyed by placeable id. */
  declare placeables: MapSchema<PlaceableState>;

  constructor() {
    super();
    this.players = new MapSchema<PlayerState>();
    this.label = "";
    this.ownerName = "";
    this.door = "shut";
    this.placeables = new MapSchema<PlaceableState>();
  }
}

defineTypes(HomeState, {
  players: { map: PlayerState },
  label: "string",
  ownerName: "string",
  door: "string",
  placeables: { map: PlaceableState },
});

export class SurfaceState extends Schema {
  /**
   * Keyed by Colyseus sessionId. View-filtered: on a 16.4 km planet each
   * client only receives entities its server-side StateView contains (the
   * room adds/removes them by distance) — two players 5 km apart never
   * hear about each other.
   */
  declare players: MapSchema<PlayerState>;
  /** Keyed by skiff id; view-filtered like players. */
  declare skiffs: MapSchema<SkiffState>;
  /** Keyed by ship id; view-filtered like players. */
  declare ships: MapSchema<ShipState>;
  /** Keyed by claim id; view-filtered like skiffs. */
  declare claims: MapSchema<ClaimState>;
  /** Keyed by structure id; view-filtered like skiffs. */
  declare structures: MapSchema<StructureState>;
  /** Keyed by creature id; view-filtered like players. */
  declare creatures: MapSchema<CreatureState>;

  constructor() {
    super();
    this.players = new MapSchema<PlayerState>();
    this.skiffs = new MapSchema<SkiffState>();
    this.ships = new MapSchema<ShipState>();
    this.claims = new MapSchema<ClaimState>();
    this.structures = new MapSchema<StructureState>();
    this.creatures = new MapSchema<CreatureState>();
  }
}

defineTypes(SurfaceState, {
  players: { map: PlayerState },
  skiffs: { map: SkiffState },
  ships: { map: ShipState },
  claims: { map: ClaimState },
  structures: { map: StructureState },
  creatures: { map: CreatureState },
});
// defineTypes silently ignores a `view` flag in field definitions (verified
// against @colyseus/schema 4.0.27 — only the schema() helper and decorators
// honor it), so the view tag is applied the way schema() does internally:
// calling view() as a plain function AFTER the field is registered.
view()(SurfaceState.prototype, "players");
view()(SurfaceState.prototype, "skiffs");
view()(SurfaceState.prototype, "ships");
view()(SurfaceState.prototype, "claims");
view()(SurfaceState.prototype, "structures");
view()(SurfaceState.prototype, "creatures");

/**
 * The Black: big, sparse, and view-filtered like the surface. Pilots'
 * PlayerStates ride along (chat, presence, carriage keep working); the
 * ships are the entities that matter out here.
 */
export class SpaceState extends Schema {
  declare players: MapSchema<PlayerState>;
  declare ships: MapSchema<ShipState>;
  /** Keyed by warden id; view-filtered like everything else out here. */
  declare wardens: MapSchema<WardenState>;

  constructor() {
    super();
    this.players = new MapSchema<PlayerState>();
    this.ships = new MapSchema<ShipState>();
    this.wardens = new MapSchema<WardenState>();
  }
}

defineTypes(SpaceState, {
  players: { map: PlayerState },
  ships: { map: ShipState },
  wardens: { map: WardenState },
});
view()(SpaceState.prototype, "players");
view()(SpaceState.prototype, "ships");
view()(SpaceState.prototype, "wardens");
